import { t } from "@/lib/i18n";

export const money = (amount, currency = "NOK", opts = {}) => {
  const n = Number(amount || 0);
  const s = new Intl.NumberFormat("en", { maximumFractionDigits: opts.decimals ?? 0 }).format(n).replace(/,/g, " ");
  return `${s} ${currency}`;
};

export const pct = (n) => `${Number(n || 0).toLocaleString("en", { maximumFractionDigits: 1 })}%`;

export const priceLabel = (item) => {
  if (!item) return "";
  if (item.behavior === "included") return t("chip.included");
  const cur = item.currency || "NOK";
  switch (item.pricing_type) {
    case "percentage":
      return `${Number(item.amount).toLocaleString("en", { maximumFractionDigits: 1 })}% ${t("price.of_revenue")}`;
    case "per_stay":
      return `${money(item.amount, cur)} ${t("price.per_stay")}`;
    case "per_guest":
      return `${money(item.amount, cur)} ${t("price.per_guest")}`;
    case "per_night":
      return `${money(item.amount, cur)} ${t("price.per_night")}`;
    case "monthly":
      return `${money(item.amount, cur)} ${t("price.monthly")}`;
    default:
      return `${money(item.amount, cur)} ${t("price.one_time")}`;
  }
};

export const fmtDate = (iso, withTime = false) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return humanize(iso);
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: d.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined });
  if (!withTime) return date;
  return `${date} · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};

/** Long form for documents: "11 September 2026" / "11 September 2026, 14:32". Never a raw ISO string. */
export const fmtDateLong = (iso, withTime = false) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return humanize(iso);
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  if (!withTime) return date;
  return `${date}, ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};

/** Last-resort presenter for machine keys: "start_page" → "Start page". Prefer a t() label. */
export const humanize = (s) => {
  if (s == null || s === "") return "";
  const str = String(s).replace(/[_-]+/g, " ").trim();
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/** Labelled lookup: t(`${ns}.${key}`) with a humanised fallback so a new enum value never leaks as a raw key. */
export const labelFor = (ns, key) => {
  if (key == null || key === "") return "";
  const k = `${ns}.${key}`;
  const s = t(k);
  return s === k ? humanize(key) : s;
};

export const sourceLabel = (s) => labelFor("source", s || "widget");
export const offerStatusLabel = (s) => labelFor("offer.status", s);
export const emailStatusLabel = (s) => labelFor("outbox.status", s);
export const roleLabel = (s) => labelFor("role", s);
export const behaviorLabel = (s) => labelFor("chip", s);
export const pricingTypeLabel = (s) => labelFor("pricing", s);

const displayNames = (type) => {
  try {
    return new Intl.DisplayNames(["en"], { type });
  } catch {
    return null;
  }
};
/** "NO" → "Norway" */
export const countryName = (code) => {
  if (!code) return "";
  const dn = displayNames("region");
  try {
    return (dn && dn.of(String(code).toUpperCase())) || String(code).toUpperCase();
  } catch {
    return String(code).toUpperCase();
  }
};
/** "NOK" → "Norwegian krone" */
export const currencyName = (code) => {
  if (!code) return "";
  const dn = displayNames("currency");
  try {
    const n = dn && dn.of(String(code).toUpperCase());
    return n && n !== code ? n : String(code).toUpperCase();
  } catch {
    return String(code).toUpperCase();
  }
};

export const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

export const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date();
  y.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today)) return t("time.today");
  if (same(d, y)) return t("time.yesterday");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined });
};

export const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return t("time.just_now");
  if (diff < 3600) return t("time.minutes_ago", { n: Math.floor(diff / 60) });
  if (diff < 86400) return t("time.hours_ago", { n: Math.floor(diff / 3600) });
  return t("time.days_ago", { n: Math.floor(diff / 86400) });
};

export const stageLabel = (s) => t(`stage.${s}`);
export const propertyTypeLabel = (s) => (s ? t(`ptype.${s}`) : "—");
export const rentalPlanLabel = (s) => (s ? t(`plan.${s}`) : "—");
export const startTimingLabel = (lead) => {
  if (!lead) return "—";
  if (lead.start_timing === "date" && lead.start_date) return fmtDate(lead.start_date);
  return lead.start_timing ? t(`start.${lead.start_timing}`) : "—";
};
export const rentedLabel = (s) => (s ? t(`rented.${s}`) : "—");

export const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

export const contrastColor = (hex) => {
  if (!hex || !/^#([0-9a-f]{3}){1,2}$/i.test(hex)) return "#FFFFFF";
  let h = hex.slice(1);
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.62 ? "#0A0A0A" : "#FFFFFF";
};
